<template>
  <div class="page-content">
    <div class="nanny-stats">
      <!-- 页面头部 -->
      <div class="page-header">
        <div class="header-left">
          <h2 class="page-title">保姆统计</h2>
          <p class="page-subtitle">监控脚本窗口与保姆设备在线状态</p>
        </div>
        <div class="header-actions">
          <el-button :loading="loading" type="primary" @click="loadData">
            <el-icon style="margin-right: 4px;"><Refresh /></el-icon>刷新
          </el-button>
        </div>
      </div>

      <!-- 统计卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon total-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ totalCount }}</div>
            <div class="stat-label">总窗口数</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon online-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ scriptOnlineCount }}</div>
            <div class="stat-label">脚本在线</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon nanny-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ nannyOnlineCount }}</div>
            <div class="stat-label">保姆在线</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon offline-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ scriptOfflineCount }}</div>
            <div class="stat-label">脚本离线</div>
          </div>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-wrapper" v-loading="loading">
        <div class="table-toolbar">
          <div class="toolbar-left">
            <button
              class="batch-stop-btn"
              :class="{ 'batch-stop-btn-disabled': selectedRows.length === 0 }"
              :disabled="selectedRows.length === 0"
              @click="handleBatchStop"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
              批量停止 <span v-if="selectedRows.length > 0" class="batch-count">({{ selectedRows.length }})</span>
            </button>
          </div>
          <div class="toolbar-right">
            <span class="result-count">共 {{ total }} 条记录</span>
          </div>
        </div>

        <el-table
          :data="items"
          stripe
          border
          style="width: 100%"
          :header-cell-style="{ textAlign: 'center', background: '#f5f7fa', color: '#606266', fontWeight: '600' }"
          :cell-style="{ textAlign: 'center' }"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="50" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" show-overflow-tooltip />
          <el-table-column prop="window_id" label="窗口ID" width="90" />
          <el-table-column prop="game" label="游戏" min-width="140" show-overflow-tooltip />
          <el-table-column prop="script_device_id" label="脚本设备" min-width="150" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="device-id">{{ row.script_device_id || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="脚本状态" width="100">
            <template #default="{ row }">
              <span class="status-badge" :class="row.script_status === 'online' ? 'status-online' : 'status-offline'">
                <span class="status-dot"></span>
                {{ row.script_status === 'online' ? '在线' : '离线' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="nanny_device_id" label="保姆设备" min-width="150" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="device-id">{{ row.nanny_device_id || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="保姆状态" width="100">
            <template #default="{ row }">
              <span class="status-badge" :class="row.nanny_status === 'online' ? 'status-online' : 'status-offline'">
                <span class="status-dot"></span>
                {{ row.nanny_status === 'online' ? '在线' : '离线' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="脚本心跳时间" min-width="160">
            <template #default="{ row }">
              <span class="time-text">{{ formatTime(row.script_update_time) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="保姆心跳时间" min-width="160">
            <template #default="{ row }">
              <span class="time-text">{{ formatTime(row.nanny_last_heartbeat_time) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template #default="{ row }">
              <button
                class="stop-btn"
                :class="{ 'stop-btn-disabled': !row.role_id }"
                :disabled="!row.role_id"
                @click="handleStopScript(row)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                停止
              </button>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          <el-pagination
            v-model:current-page="page"
            v-model:page-size="size"
            :page-sizes="[20, 50, 100, 200]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { getNannyDevices, stopNannyScript } from '../api/nanny'

export default {
  name: 'NannyStats',
  components: { Refresh },
  setup() {
    const loading = ref(false)
    const items = ref([])
    const selectedRows = ref([])
    const page = ref(1)
    const size = ref(100)
    const total = ref(0)

    // 统计卡片计算
    const totalCount = computed(() => items.value.length)
    const scriptOnlineCount = computed(() => items.value.filter(i => i.script_status === 'online').length)
    const nannyOnlineCount = computed(() => items.value.filter(i => i.nanny_status === 'online').length)
    const scriptOfflineCount = computed(() => items.value.filter(i => i.script_status !== 'online').length)

    // 时间格式化：2026-02-04T21:07:44 → 02-04 21:07:44
    const formatTime = (timeStr) => {
      if (!timeStr) return '-'
      try {
        const s = timeStr.replace('T', ' ')
        const parts = s.split(' ')
        if (parts.length === 2) {
          const datePart = parts[0].substring(5) // MM-DD
          const timePart = parts[1].length > 8 ? parts[1].substring(0, 8) : parts[1]
          return datePart + ' ' + timePart
        }
        return s
      } catch {
        return timeStr
      }
    }

    const loadData = async () => {
      loading.value = true
      try {
        const res = await getNannyDevices({ page: page.value, size: size.value })
        const data = res.data || {}
        items.value = data.items || []
        total.value = data.total || 0
      } catch (e) {
        console.error('加载保姆统计失败:', e)
        ElMessage.error(e?.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = async (newPage) => {
      page.value = newPage
      await loadData()
    }

    const handleSizeChange = async (newSize) => {
      size.value = newSize
      page.value = 1
      await loadData()
    }

    const handleSelectionChange = (rows) => {
      selectedRows.value = rows || []
    }

    const handleStopScript = async (row) => {
      if (!row || !row.role_id) return
      try {
        await ElMessageBox.confirm(
          `确定要停止窗口 ${row.window_id || ''} 的脚本吗？`,
          '确认停止脚本',
          { confirmButtonText: '确定停止', cancelButtonText: '取消', type: 'warning' }
        )
        await stopNannyScript(row.role_id)
        ElMessage.success('已下发停止脚本命令')
        await loadData()
      } catch (e) {
        if (e === 'cancel') return
        console.error('停止脚本失败:', e)
        ElMessage.error(e?.message || '停止脚本失败')
      }
    }

    const handleBatchStop = async () => {
      const roleIds = selectedRows.value.filter(r => r && r.role_id).map(r => r.role_id)
      if (roleIds.length === 0) {
        ElMessage.warning('请先选择要停止的窗口')
        return
      }
      try {
        await ElMessageBox.confirm(
          `确定要为选中的 ${roleIds.length} 个窗口下发停止脚本命令吗？`,
          '批量停止脚本',
          { confirmButtonText: '确定停止', cancelButtonText: '取消', type: 'warning' }
        )
        let success = 0
        let fail = 0
        for (const roleId of roleIds) {
          try {
            await stopNannyScript(roleId)
            success++
          } catch {
            fail++
          }
        }
        if (fail === 0) {
          ElMessage.success(`已为 ${success} 个窗口下发停止脚本命令`)
        } else {
          ElMessage.warning(`成功 ${success} 个，失败 ${fail} 个`)
        }
        await loadData()
      } catch (e) {
        if (e === 'cancel') return
        console.error('批量停止脚本失败:', e)
        ElMessage.error(e?.message || '批量停止脚本失败')
      }
    }

    onMounted(() => {
      loadData()
    })

    return {
      loading, items, selectedRows, page, size, total,
      totalCount, scriptOnlineCount, nannyOnlineCount, scriptOfflineCount,
      formatTime, loadData,
      handlePageChange, handleSizeChange, handleSelectionChange,
      handleBatchStop, handleStopScript
    }
  }
}
</script>

<style scoped>
.page-content {
  padding-bottom: 32px;
  box-sizing: border-box;
}

/* ===== 页面头部 ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e9ea;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1d1d1f;
}

.page-subtitle {
  margin: 4px 0 0;
  color: #86868b;
  font-size: 13px;
}

/* ===== 统计卡片 ===== */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
  transition: box-shadow 0.2s, transform 0.2s;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
}

.total-icon {
  background: #eef2ff;
  color: #4f6cf7;
}

.online-icon {
  background: #ecfdf5;
  color: #10b981;
}

.nanny-icon {
  background: #fef3e2;
  color: #f59e0b;
}

.offline-icon {
  background: #fef2f2;
  color: #ef4444;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1d1d1f;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: #86868b;
  margin-top: 2px;
}

/* ===== 表格区域 ===== */
.table-wrapper {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-count {
  margin-left: 2px;
  font-weight: 400;
}

.result-count {
  font-size: 13px;
  color: #86868b;
}

/* 设备ID文字 */
.device-id {
  font-size: 12px;
  color: #606266;
  font-family: 'SFMono-Regular', Consolas, monospace;
}

/* 状态标签 */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}

.status-online {
  background: #ecfdf5;
  color: #059669;
}

.status-online .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.status-offline {
  background: #f3f4f6;
  color: #9ca3af;
}

.status-offline .status-dot {
  background: #d1d5db;
}

/* 时间文字 */
.time-text {
  font-size: 13px;
  color: #606266;
  font-family: 'SFMono-Regular', Consolas, monospace;
}

/* ===== 批量停止按钮 ===== */
.batch-stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 8px rgba(238, 90, 36, 0.3);
  white-space: nowrap;
}

.batch-stop-btn:hover {
  background: linear-gradient(135deg, #ff5252, #e04010);
  box-shadow: 0 4px 14px rgba(238, 90, 36, 0.4);
  transform: translateY(-1px);
}

.batch-stop-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(238, 90, 36, 0.3);
}

.batch-stop-btn svg {
  flex-shrink: 0;
}

.batch-stop-btn .batch-count {
  font-weight: 400;
  margin-left: 1px;
}

.batch-stop-btn-disabled,
.batch-stop-btn:disabled {
  background: #e0e0e0;
  color: #aaa;
  cursor: not-allowed;
  box-shadow: none;
}

.batch-stop-btn-disabled:hover,
.batch-stop-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

/* ===== 行内停止按钮 ===== */
.stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 6px rgba(238, 90, 36, 0.3);
  white-space: nowrap;
}

.stop-btn:hover {
  background: linear-gradient(135deg, #ff5252, #e04010);
  box-shadow: 0 4px 12px rgba(238, 90, 36, 0.4);
  transform: translateY(-1px);
}

.stop-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(238, 90, 36, 0.3);
}

.stop-btn svg {
  flex-shrink: 0;
}

.stop-btn-disabled,
.stop-btn:disabled {
  background: #e0e0e0;
  color: #aaa;
  cursor: not-allowed;
  box-shadow: none;
}

.stop-btn-disabled:hover,
.stop-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

/* 分页 */
.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .stat-card {
    padding: 14px 16px;
  }

  .stat-value {
    font-size: 20px;
  }

  .table-wrapper {
    padding: 12px;
  }

  .table-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
